/**
 * R2-5 — WHAT EACH GAME IS CALLED, on the client, without loading a board.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS ITS OWN FILE AND NOT A FIELD ON THE BOARD REGISTRY
 *
 * `titleFor` (`lib/title.ts`) has to name a game SYNCHRONOUSLY, from the
 * pathname alone, in a `<RouteTitle/>` that is mounted eagerly at the top of
 * the app. It cannot wait for `GET /api/daily-challenges/games` — a title that arrives
 * a network round-trip late is a title that flashes the wrong thing on every
 * open — and it cannot read `DAILY_CHALLENGE_BOARDS`, because importing the board
 * registry from an eager module drags all nine boards out of the lazy
 * `DailyChallengesPage` chunk and into the entry chunk. Measured on this tree: the
 * entry chunk is 4.58 kB and the daily challenges chunk is 164 kB, so that import
 * would grow the first byte every visitor downloads — including a visitor who
 * only ever opens trivia — by about 36×.
 *
 * So the names live HERE: a leaf module with no imports, which costs a few
 * hundred bytes in the entry chunk and pulls in nothing else.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THIS IS NOT A SECOND SOURCE OF TRUTH
 *
 * `src/daily-challenges/registry.ts` on the SERVER decides what a game is called; this
 * map is the client's copy of exactly those strings, and
 * `tests/daily-challenges-ui-contract.test.ts` § R2-5 pins the two together in both
 * directions — every game in the server's test registry has an entry here, every
 * entry here is a real game, and every pair of strings is identical. Renaming a
 * game on the server without updating this file is a failing unit test, not a
 * wrong tab title discovered in a screenshot three weeks later.
 *
 * The names are NOT derived from the ids. `mini-sudoku` → "Mini Sudoku" happens
 * to fall out of title-casing; `tap-ten` → "Tap Ten" does too. That is a
 * coincidence of the first nine games, not a rule, and the first game called
 * "Zip It!" or "Cross-Climb" would break it silently.
 */

/**
 * Display name by game id — including the platform's two scaffolding entries
 * (`tap-ten`, `not-yet`), which are reachable by anyone who types the id into
 * the URL bar and therefore need a title like anything else.
 */
export const DAILY_CHALLENGE_NAMES: Record<string, string> = {
  queens: "Queens",
  tango: "Tango",
  zip: "Zip",
  crossclimb: "Crossclimb",
  pinpoint: "Pinpoint",
  "mini-sudoku": "Mini Sudoku",
  patches: "Patches",
  wend: "Wend",
  wordle: "Wordle",
  "color-sort": "Color Sort",
  "tap-ten": "Tap Ten",
  "not-yet": "Not Yet",
};

/** The game's display name, or `null` for an id this build does not know. */
export function dailyChallengeName(id: string | undefined | null): string | null {
  if (!id) return null;
  return DAILY_CHALLENGE_NAMES[id] ?? null;
}
